import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { landAxios } from '@Root/utils/landAxios'
import MyHouseReviewSummaryResponse from '@Types/entities/MyHouseReviewSummaryResponse'
import MyHouseReviewParam from '@Types/entities/MyHouseReviewParam'
import MyHouseReviewListResponse from '@Types/entities/MyHouseReviewListResponse'
import MyhouseReviewResponse from '@Types/entities/MyhouseReviewResponse'
import { FETCH_STATUS } from '@Root/contants'

export interface ReviewParam {
  complexNo: number
  reviewSequence: number
}

export type MyhouseReviewDetail = {
  fetchStatus: FETCH_STATUS
} & MyhouseReviewResponse

export const fetchReview = createAsyncThunk(
  'myhouse/fetchReview',
  async (params: ReviewParam) => await (await landAxios.get(`/myhouse/complexes/${params.complexNo}/reviews/${params.reviewSequence}`)).data,
)

export const fetchReviewList = createAsyncThunk(
  'myhouse/fetchReviews',
  async (params: MyHouseReviewParam) =>
    await (
      await landAxios.get(`/myhouse/complexes/${params.complexNo}/reviews`, {
        params: { ...params, complexNo: null },
      })
    ).data,
)

export const fetchInitialReviewList = createAsyncThunk(
  'myhouse/fetchInitialReviews',
  async (params: MyHouseReviewParam) =>
    await (
      await landAxios.get(`/myhouse/complexes/${params.complexNo}/reviews`, {
        params: { ...params, complexNo: null },
      })
    ).data,
)

export const fetchMyReview = createAsyncThunk(
  'myhouse/fetchMyReview',
  async (params: ReviewParam) => await (await landAxios.get(`/myhouse/complexes/${params.complexNo}/my/reviews/${params.reviewSequence}`)).data,
)

export type MyHouseReviewState = {
  complexNo: number
  complexName: string
  hasMore: boolean
  totalCount: number
  list: MyHouseReviewSummaryResponse[]
  listFetchStatus: FETCH_STATUS
  detail: MyhouseReviewDetail
}

export const initialState: MyHouseReviewState = {
  listFetchStatus: FETCH_STATUS.INITIAL,
  list: [] as MyHouseReviewSummaryResponse[],
  detail: { fetchStatus: FETCH_STATUS.INITIAL } as MyhouseReviewDetail,
} as MyHouseReviewState

const myhouseReviewSlice = createSlice({
  name: 'myhouseReview',
  initialState,
  reducers: {
    updateReviewSequence: (state: MyHouseReviewState, action: PayloadAction<number>) => {
      state.detail.sequence = action.payload
    },
    updateReviewDetail: (state: MyHouseReviewState, action: PayloadAction<MyhouseReviewDetail>) => {
      state.detail = action.payload
    },
    updateReviewList: (state: MyHouseReviewState) => {
      state.listFetchStatus = FETCH_STATUS.INITIAL
      state.list = []
    },
  },

  extraReducers: {
    [fetchReviewList.fulfilled.type]: (state: MyHouseReviewState, action: PayloadAction<MyHouseReviewListResponse>) => {
      state.listFetchStatus = FETCH_STATUS.SUCCESS
      state.complexNo = action.payload.complexNo
      state.complexName = action.payload.complexName
      state.hasMore = action.payload.hasMore
      state.totalCount = action.payload.totalCount
      state.list = [...state.list, ...action.payload.reviewList]
    },
    [fetchInitialReviewList.fulfilled.type]: (state: MyHouseReviewState, action: PayloadAction<MyHouseReviewListResponse>) => {
      state.listFetchStatus = FETCH_STATUS.SUCCESS
      state.complexNo = action.payload.complexNo
      state.complexName = action.payload.complexName
      state.hasMore = action.payload.hasMore
      state.totalCount = action.payload.totalCount
      state.list = action.payload.reviewList
    },
    [fetchReview.fulfilled.type]: (state: MyHouseReviewState, action: PayloadAction<MyhouseReviewResponse>) => {
      state.detail = {
        ...action.payload,
        fetchStatus: FETCH_STATUS.SUCCESS,
      }
    },
    [fetchMyReview.fulfilled.type]: (state: MyHouseReviewState, action: PayloadAction<MyhouseReviewResponse>) => {
      state.detail = {
        ...action.payload,
        fetchStatus: FETCH_STATUS.SUCCESS,
      }
    },

    [fetchReviewList.pending.type]: (state: MyHouseReviewState) => {
      state.listFetchStatus = FETCH_STATUS.PENDING
    },
    [fetchInitialReviewList.pending.type]: (state: MyHouseReviewState) => {
      state.listFetchStatus = FETCH_STATUS.PENDING
    },
    [fetchReview.pending.type]: (state: MyHouseReviewState) => {
      state.detail.fetchStatus = FETCH_STATUS.PENDING
    },
    [fetchMyReview.pending.type]: (state: MyHouseReviewState) => {
      state.detail.fetchStatus = FETCH_STATUS.PENDING
    },

    [fetchReviewList.rejected.type]: (state: MyHouseReviewState) => {
      state.listFetchStatus = FETCH_STATUS.FAILURE
    },
    [fetchInitialReviewList.rejected.type]: (state: MyHouseReviewState) => {
      state.listFetchStatus = FETCH_STATUS.FAILURE
    },
    [fetchReview.rejected.type]: (state: MyHouseReviewState) => {
      state.detail = {
        ...initialState.detail,
        fetchStatus: FETCH_STATUS.FAILURE,
      }
    },
    [fetchMyReview.rejected.type]: (state: MyHouseReviewState) => {
      state.detail = {
        ...initialState.detail,
        fetchStatus: FETCH_STATUS.FAILURE,
      }
    },
  },
})

export const { updateReviewSequence, updateReviewDetail, updateReviewList } = myhouseReviewSlice.actions
export default myhouseReviewSlice.reducer
